<script setup>
import { computed } from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import LandingResources from "./includes/LandingResources.vue";
import EditableTextEditorField
    from "../../../portal-editable-fields/EditableTextEditorField.vue";
import EditableImageField from "../../../portal-editable-fields/EditableImageField.vue";
import EditableSymbolField from "../../../portal-editable-fields/EditableSymbolField.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const showSymbol = computed(() =>
    portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general?.program_branding_type === 'symbol'
    && portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol
    && portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol !== portalContentAndDesignStore.getFileUploadNoImageRoute
);
const showLogo = computed(() =>
    portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general?.program_branding_type === 'logo'
    && portalContentAndDesignStore.srcRoutes.portal_layout_file_logo
    && portalContentAndDesignStore.srcRoutes.portal_layout_file_logo !== portalContentAndDesignStore.getFileUploadNoImageRoute
);

const buttonBgSoft = computed(() => `bg-${portalContentAndDesignStore.getPageContentTextColorClass} bg-opacity-5`);

const layoutPadding = computed(() => {
    if (portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout === 'one' && portalContentAndDesignStore.srcRoutes.public_page_background_image) return 'px-8 lg:px-14';
    return 'px-8 lg:px-14';
});

const symbolCorners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout === 'one' ? 'rounded' : portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none' ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners : '');
const commissionBlockCorners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout === 'one' ? 'rounded' : portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none' ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners : '');

const showSignupOrLeadLinks = computed(() => {
    return portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.display_submit_lead_link || portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.display_signup_and_login_links;
});

const disableRegistration = computed(() => portalContentAndDesignStore.portalLayoutSettings.referral_portal_portal_disable_registrations_via_portal || false);

const programLandingPageUrl = computed(() => portalContentAndDesignStore.getLandingPageSettings.landing_page_url || '/');

const layoutCorners = computed(() => {
    return portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout === "one"
        ? "rounded" : portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== "none"
            ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners
            : "";
});

</script>

<template>
    <!-- Program details (static) -->
    <div
        v-if="portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general"
    >
        <div class="space-y-6 pb-8 lg:pb-12 pt-8 lg:pt-20" :class="[ layoutPadding ]">
            <div class="lg:flex gap-6">
                <!-- Symbol -->
                <editable-symbol-field
                    v-if="showSymbol"
                    :symbol-wrapper-classes="[
                        'relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-white bg-cover bg-center',
                        symbolCorners
                    ]"
                    symbol-name="portal_layout_file_symbol"
                    v-model:symbol-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol"
                    :symbol-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                    :symbol-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                />

                <!-- Logo -->
                <div
                    v-if="showLogo"
                    class="relative w-auto"
                    style="max-width: 256px"
                >
                    <editable-image-field
                        v-model:image-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo"
                        img-name="portal_layout_file_logo"
                        title="Logo"
                        :image-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                        :image-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                        img-class="w-auto h-auto"
                    ></editable-image-field>
                </div>

                <!-- Text + Buttons -->
                <div class="flex-1 space-y-4 mt-4 lg:mt-0">
                    <div :class="[portalContentAndDesignStore.getPageContentTextColorClass]">
                        <div class="flex items-center justify-between gap-6">

                          <editable-text-field
                              text-type="h2"
                              v-model="portalContentAndDesignStore.landingPageSettings.landing_page_resources.general.introduction"
                              class="mb-1"
                          ></editable-text-field>

                            <editable-btn
                                size="small"
                                class="rounded-full hidden lg:flex"
                                :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                                :href="programLandingPageUrl"
                                link-target="_blank"
                                icon-name="arrow-right-up"
                                icon-right
                            />
                        </div>

                        <editable-text-editor-field
                            v-if="portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.description"
                            :class="[
                                'partnero-page-content-section text-md space-y-2 text-opacity-80',
                                portalContentAndDesignStore.getPageContentTextColorClass
                            ]"
                            v-model="portalContentAndDesignStore.landingPageSettings.landing_page_resources.general.description"
                        />

                        <editable-btn
                            size="small"
                            class="w-full mt-4 flex lg:hidden"
                            :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                        >
                          <span>
                              <a class="whitespace-nowrap" :href="programLandingPageUrl" target="_blank">
                                  {{ portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website }}
                              </a>
                          </span>
                          <icon class="ml-2" name="arrow-right-up"></icon>
                        </editable-btn>
                    </div>

                    <!-- Sign Up / Login Buttons -->
                    <div
                        v-if="showSignupOrLeadLinks"
                        class="flex items-center gap-2"
                    >
                        <editable-btn
                            v-if="portalContentAndDesignStore.portalLayoutSettings.program_leads_form_enabled && portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.display_submit_lead_link"
                            class="mr-2"
                            size="small"
                            :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                            :href="portalContentAndDesignStore.getLeadsRoute"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_leads_submit_a_lead"
                        />

                        <template v-if="portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.display_signup_and_login_links">
                            <editable-btn
                                size="small"
                                :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                                :href="portalContentAndDesignStore.getLoginRoute"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_log_in"
                            />

                            <editable-text-field
                                v-if="!disableRegistration"
                                class="text-sm"
                                :class="portalContentAndDesignStore.getPageContentTextColorClass"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_or"
                                text-type="span"
                            >
                            </editable-text-field>

                            <editable-btn
                                v-if="!disableRegistration"
                                size="small"
                                :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                                :href="portalContentAndDesignStore.getRegisterRoute"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_sign_up"
                            />
                        </template>
                    </div>
                </div>
            </div>
            <!-- Commission Block -->
            <div
                v-if="portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.display_commission"
                class="bg-black bg-opacity-5 p-6"
                :class="commissionBlockCorners"
            >
                <div class="flex items-center justify-between gap-6">
                    <div :class="portalContentAndDesignStore.getPageContentTextColorClass">
                        <editable-text-field
                            class="text-xl font-semibold"
                            text-type="custom"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_earn"
                        ></editable-text-field>

                        <editable-text-field v-if="portalContentAndDesignStore.getLandingPageSettings?.landing_page_resources?.general.commission_text"
                            text-type="custom"
                            class="text-base lg:text-lg"
                            v-model="portalContentAndDesignStore.landingPageSettings.landing_page_resources.general.commission_text"
                        ></editable-text-field>
                        <div v-else class="text-base lg:text-lg">
                            {{ portalContentAndDesignStore.programCommissionDescription }}
                        </div>
                    </div>
                    <div class="hidden lg:flex">
                      <editable-text-field
                          text-type="a"
                          v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_join_now"
                          custom-classes="flex items-center gap-2 text-sm rounded py-2 px-4 hover:bg-opacity-0 border"
                          :class="[buttonBgSoft, portalContentAndDesignStore.getPageContentTextColorClass]"
                          :href="portalContentAndDesignStore.getRegisterRoute"
                          icon-name="arrow-right-up"
                          icon-right
                      >
                      </editable-text-field>
                    </div>
                </div>
                <div class="flex justify-start lg:hidden mt-4">
                  <editable-text-field
                      text-type="a"
                      v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_join_now"
                      custom-classes="flex items-center gap-2 text-sm rounded py-2 px-4 hover:bg-opacity-0 border"
                      :class="[buttonBgSoft, portalContentAndDesignStore.getPageContentTextColorClass]"
                      :href="portalContentAndDesignStore.getRegisterRoute"
                      icon-name="arrow-right-up"
                      icon-right
                    >
                  </editable-text-field>

                </div>
            </div>
        </div>

        <landing-resources />

    </div>
    <div v-else>
        <div class="bg-white h-96 flex items-center justify-center" :class="layoutCorners">
            <div class="space-y-2 text-center text-gray-500">
                <p class="font-bold">Please enable portal's Program page in the app.</p>
                <p class="text-sm">Add a title to your portal under Partner portal > Pages & Content > Public pages > Program page. This is sufficient to activate it.</p>
                <p class="text-sm">Once done, return here to start building your Landing Page.</p>
            </div>
        </div>
    </div>
</template>


